/* eslint-disable import/no-deprecated */
import { useFocusVisible, useHover } from '@fullstory/react-hooks';
import { classNames } from '@fullstory/util/fmt';
import React, { ComponentProps, useEffect, useId, useRef } from 'react';
import { Flex } from '../../legacy/Flex';
import { identifyComponent } from '../../util';
import { Icon } from '../Icon';
import { Text } from '../Text';
import css from './index.module.scss';

type TargetSize = 'small' | 'large';

export interface CheckboxProps {
  testId?: string;
  /** Text displayed next to the checkbox */
  label?: string | React.ReactNode;
  /** Text displayed under the main label text */
  sublabel?: string;
  /** Set the value of the checkbox. If set, this will become a controlled component. */
  checked: boolean;
  /** If true, the component appears indeterminate and aria-checked="mixed" */
  indeterminate?: boolean;
  /** Callback fired when the value of the checkbox changes. */
  onChange: (checked: boolean) => void;
  /** If disabled, the checkbox cannot be interacted with. */
  disabled?: boolean;
  /**
   * Callback fired when the checkbox is clicked.
   *
   * Generally `onClick` shouldn't be needed. Instead use `onChange`.
   * However, in some cases, you might want to do something with the click
   * event, like stopping propagation. But, `onChange` should always be used along with `onClick`.
   * */
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  /** Optionally hide the label but keep it in the DOM for accessibility */
  hideLabel?: boolean;
  /* Determines the touch/click target size */
  targetSize?: TargetSize;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  testId = 'checkbox',
  label,
  sublabel,
  checked,
  indeterminate = false,
  onChange = () => {},
  onClick,
  disabled = false,
  hideLabel = false,
  targetSize = 'large',
}: CheckboxProps) => {
  const prevStateRef = useRef<'checkmark' | 'minus'>();
  const { hovered, hoverProps } = useHover();
  const { focusVisible, ...focusProps } = useFocusVisible();
  let labelColor: ComponentProps<typeof Text>['color'] = 'default';
  let sublabelColor: ComponentProps<typeof Text>['color'] = 'secondary';
  if (disabled) {
    labelColor = 'disabled';
    sublabelColor = 'disabled';
  } else if (hovered || focusVisible) {
    labelColor = 'emphasized';
  }

  useEffect(() => {
    if (checked || indeterminate) {
      prevStateRef.current = checked ? 'checkmark' : 'minus';
    }
  });

  const prevFilledState = prevStateRef.current ?? 'checkmark';
  const iconToRender = indeterminate ? 'minus' : checked ? 'checkmark' : prevFilledState;

  const id = useId();
  const labelId = `label-${id}`;
  const checkboxId = `checkbox-${id}`;

  return (
    <Flex
      alignItems="flex-start"
      justifyContent="flex-start"
      data-component="Checkbox"
      className={classNames(css.root, {
        [css.focused]: focusVisible,
        [css.hovered]: hovered,
        [css.labeled]: !!label && !hideLabel,
        [css.large]: targetSize === 'large',
      })}
      {...hoverProps}
      {...identifyComponent('checkbox')}
    >
      <span
        className={classNames(css.checkboxTarget, {
          [css.hovered]: hovered,
          [css.disabled]: disabled,
          [css.large]: targetSize === 'large',
        })}
      >
        <input
          type="checkbox"
          className={css.checkboxInput}
          id={checkboxId}
          checked={checked}
          aria-checked={indeterminate ? 'mixed' : checked}
          aria-labelledby={labelId}
          disabled={disabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
          onClick={onClick}
          data-testid={testId}
          {...hoverProps}
          {...focusProps}
        />
        <span
          className={classNames(css.checkboxBorder, {
            [css.hovered]: hovered,
            [css.disabled]: disabled,
          })}
        />
        <span
          className={classNames(css.checkboxIcon, {
            [css.disabled]: disabled,
            [css.filled]: !!checked || !!indeterminate,
          })}
        >
          <Icon name={iconToRender} size={16} color={disabled ? 'disabled' : 'default-inverse'} />
        </span>
      </span>
      {label &&
        (hideLabel ? (
          <label htmlFor={checkboxId} id={labelId} style={{ display: 'none' }} aria-hidden>
            {label}
          </label>
        ) : (
          <Flex
            className={classNames(css.label)}
            justifyContent="flex-start"
            alignItems="flex-start"
            column
          >
            <label
              htmlFor={checkboxId}
              id={labelId}
              className={classNames({ [css.hovered]: hovered })}
            >
              <Text tag="span" appearance="bodyMedium" color={labelColor}>
                {label}
              </Text>
            </label>
            {sublabel && (
              <Text tag="span" appearance="bodyMedium" color={sublabelColor}>
                {sublabel}
              </Text>
            )}
          </Flex>
        ))}
    </Flex>
  );
};
